/**
 * Questionnaire Page
 * 
 * Comprehensive financial questionnaire that gathers all the information
 * needed for a personalized life financial plan.
 */

import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { QuestionnaireWizard } from '@/components/questionnaire'
import { Button } from '@/components/ui/button'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { visitPage, setLifePlanData } from '@/store'
import { syncQuestionnaireToLifePlan } from '@/services/questionnaireSync'
import { ArrowLeft } from 'lucide-react'

export default function Questionnaire() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { isCompleted, progress } = useAppSelector((state) => state.questionnaire)

  // Track page visit
  React.useEffect(() => {
    dispatch(visitPage('questionnaire'))
  }, [dispatch])

  const handleComplete = React.useCallback(() => {
    // Sync questionnaire answers to lifePlan state
    const lifePlanData = syncQuestionnaireToLifePlan(progress.answers)
    dispatch(setLifePlanData(lifePlanData))
    
    // Navigate to the timeline/dashboard after completion
    navigate('/timeline')
  }, [navigate, dispatch, progress.answers])

  return (
    <div className="container max-w-6xl py-6">
      <PageHeader
        title="Financial Questionnaire"
        description="Help us understand your complete financial picture to build your personalized life plan"
      >
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          {progress.startedAt && !isCompleted && (
            <Button variant="outline" onClick={() => navigate('/timeline')}>
              Continue Later
            </Button>
          )}
        </div>
      </PageHeader>

      <div className="mt-8">
        <QuestionnaireWizard onComplete={handleComplete} />
      </div>
    </div>
  )
}
